/**
 * Момент цитаты критерия в записи (#862; общий для панели звонка и отчёта в
 * CRM с #2516).
 *
 * Не поиском текста. Короткая цитата — «да», приветствие, повтор — встречается
 * в нескольких сегментах, и поиск подстроки уводит на первый (находка Codex на
 * #1121). Вместо этого восстанавливается ТА ЖЕ нумерация реплик, которую шаг
 * оценки напечатал модели (`turnsFromTranscript` + `prepareTurns`), и по
 * номеру из доказательства находится сегмент, из которого реплика взята.
 *
 * Своя копия нумерации у потребителя была бы худшим из вариантов: она молча
 * разошлась бы с промптом, и номера начали бы указывать на чужие реплики.
 *
 * Единица времени — та, в которой пришли сегменты: хранимая диаризация у
 * панели — миллисекунды, сегменты конвейера — секунды. Переводит вызывающий.
 */
import { prepareTurns, flattenTurnText } from './turns';
import { turnsFromTranscript } from './transcript-turns';

/** Минимум, который нужен поиску момента: начало реплики и её текст. */
export interface EvidenceMomentSegment {
  start: number;
  text?: string;
}

/** Реплика, найденная по номеру: где она начинается и что в ней сказано. */
export interface EvidenceTurnMoment {
  start: number;
  text: string;
}

/** Номер реплики → её момент. Строится один раз на набор сегментов. */
export type EvidenceMomentIndex = ReadonlyMap<number, EvidenceTurnMoment>;

const EMPTY_INDEX: EvidenceMomentIndex = new Map();

/** Отображение «номер реплики → момент записи» по сегментам диаризации. */
export function buildEvidenceMomentIndex(
  segments: readonly EvidenceMomentSegment[] | null | undefined
): EvidenceMomentIndex {
  if (!segments || segments.length === 0) return EMPTY_INDEX;

  const raw = turnsFromTranscript({
    // Пустой текст, а не транскрипт: запасной путь по строкам даёт реплики без
    // времени, и момент по ним всё равно не построить.
    transcript: '',
    diarization: segments,
  });
  const { turns } = prepareTurns(raw.turns);

  const index = new Map<number, EvidenceTurnMoment>();
  for (const turn of turns) {
    if (turn.sourceIndex === undefined) continue;
    const segment = segments[turn.sourceIndex];
    // Нечисловое начало сегмента пропускаем: «момент неизвестен» выражается
    // отсутствием строки, а не `NaN`.
    if (!segment || !Number.isFinite(segment.start)) continue;
    index.set(turn.turn, { start: segment.start, text: flattenTurnText(turn.text) });
  }
  return index;
}

/**
 * Начало реплики цитаты (в единицах сегментов) или `null`.
 *
 * `null` означает «не доказано»: показать не тот момент хуже, чем не показать
 * никакого. Так выходит, когда номера нет в нумерации, цитата пуста (проверять
 * нечем) или найденная реплика её не содержит — последнее и есть страховка от
 * расхождения: если запись перетранскрибировали после оценки, нумерация уже
 * другая.
 */
export function momentOfEvidence(
  evidence: { turn: number; quote: string },
  index: EvidenceMomentIndex
): number | null {
  const moment = index.get(evidence.turn);
  if (!moment) return null;

  const quote = flattenTurnText(evidence.quote);
  if (quote === '' || !moment.text.includes(quote)) return null;

  return moment.start;
}
